import * as React from "react";

import { Box, type BoxProps } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { Separator } from "../../components/ui/separator";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import type { GlyphName } from "../../components/ui/icon-registry";
import type { Omittable } from "../../lib/props";
import { labelControl } from "../../lib/label-control";
import { cn } from "../../lib/utils";

// ---------------------------------------------------------------------------
// OptionPanel — the settings-list grammar
// ---------------------------------------------------------------------------
//
// A titled stack of rows, each one a label with a control on the trailing
// edge. Three row shapes cover every settings surface:
//
//   OptionPanelRow      label … control (Switch, ToggleGroup, value Text)
//   OptionPanelNavRow   icon label … value chevron   — drills, or discloses
//   OptionPanelActionRow  icon label                 — performs an action
//
// The rows share one rhythm (32px floor, 8px inset, `sm` radius on the hover
// wash) so a panel mixing all three reads as one list. Sections carry the
// horizontal padding, never the rows — an `OptionPanelSeparator` placed
// BETWEEN sections spans the full panel width. The section's 16px inset plus
// the row's 8px puts every label 24px off the panel edge, with the wash
// bleeding 8px past it.
//
// The two pressable shapes are real `<button>` elements across the row's full
// width: the whole row is the target, keyboard reaches it, and `disabled`
// blocks the press rather than only dimming it.
//
// Domain-free: the panel knows nothing about what it configures. Labels,
// icons and controls are all the host's.

/**
 * The section's horizontal inset in pixels — the distance from the panel's
 * edge to a row's edge (`paddingX="md"` below). A popup a row opens BESIDE the
 * panel offsets by it, so the popup's edge meets the panel's edge rather than
 * the row's and nothing floats over the panel's own margin.
 */
export const OPTION_PANEL_SECTION_INSET = 16;

export type OptionPanelSectionProps = Omittable<{
  /** Small heading above the rows. Omit for an untitled group. */
  title?: React.ReactNode;
  children: React.ReactNode;
}>;

/** A titled group of option rows, carrying the panel's horizontal inset —
 *  `md` is the 16px {@link OPTION_PANEL_SECTION_INSET} names. */
export function OptionPanelSection({ title, children }: OptionPanelSectionProps) {
  return (
    <Box direction="column" paddingX="md" paddingY="xs" gap="2xs">
      {title ? (
        <Box paddingX="sm" paddingTop="2xs" paddingBottom="2xs">
          {typeof title === "string" ? (
            <Text size="xs" weight="medium" variant="faint">
              {title}
            </Text>
          ) : (
            title
          )}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

/** The hairline between section groups. It sits outside the sections'
 *  horizontal inset, so it spans the panel edge to edge; its own `xs`
 *  padding plus each section's makes an 8px reveal on either side. */
export function OptionPanelSeparator() {
  return (
    <Box paddingY="xs">
      <Separator />
    </Box>
  );
}

export type OptionPanelRowProps = Omittable<{
  label: React.ReactNode;
  /** Leading glyph. Omitted rows start at the label — a section of plain
   *  switch rows needs no icon gutter. */
  icon?: GlyphName;
  /** A leading picture in the glyph's place — a `PreviewArtThumbnail` of
   *  what the row switches on. Takes the seat `icon` would; pass one. */
  visual?: React.ReactNode;
  /**
   * The row's why, behind an info glyph after the label. The sentence lives
   * in a tooltip so the row stays ONE rung tall like its neighbours — a
   * subtext line would make it two and break the list's rhythm. Read on
   * hover or focus; the glyph is the trigger, and it opens with no delay.
   */
  hint?: string;
  /** Dims the row and blocks its control's pointer events. */
  disabled?: boolean;
  /** The trailing control. */
  children?: React.ReactNode;
}>;

/** Label on the leading edge, control on the trailing edge. */
export function OptionPanelRow({
  label,
  icon,
  visual,
  hint,
  disabled,
  children,
}: OptionPanelRowProps) {
  const labelId = React.useId();
  return (
    <Box
      direction="row"
      align="center"
      gap="sm"
      minHeight={32}
      paddingX="sm"
      rounded="sm"
      opacity={disabled ? 50 : undefined}
    >
      {visual ?? (icon ? <Icon name={icon} size="sm" color="muted" /> : null)}
      <Box id={labelId} direction="row" align="center" gap="xs" shrink fill="flex" minWidth={0}>
        {typeof label === "string" ? (
          <Text size="sm" truncate={1}>
            {label}
          </Text>
        ) : (
          label
        )}
        {hint ? (
          // Its own zero-delay group: the glyph carries no meaning of its own
          // — the sentence behind it IS the glyph — so a hover wait reads as a
          // mark that does nothing. The delay lives on a Provider because
          // that is where Base UI reads it; scoped here, the surrounding
          // app's delay still governs every other tooltip on the row.
          <TooltipProvider delay={0}>
            <Tooltip>
              <TooltipTrigger render={<Box direction="row" align="center" />}>
                <Icon color="muted" name="info" size="xs" />
              </TooltipTrigger>
              <TooltipPopup>{hint}</TooltipPopup>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </Box>
      {labelControl(children, labelId)}
    </Box>
  );
}

export type OptionPanelNavRowProps = Omittable<{
  label: React.ReactNode;
  icon?: GlyphName;
  /** Current value, shown before the chevron as the row's quiet readout:
   *  faint ink on the 12px line, a rung under the label in both tone and
   *  size so the two never compete. */
  value?: React.ReactNode;
  /**
   * Present, the trailing glyph becomes a DISCLOSURE: it rests pointing right
   * and rotates down while `expanded`, and the row reports `aria-expanded`.
   * Omit it for a row that navigates — a chevron that never turns is the
   * promise that pressing takes you somewhere else, and a row whose content
   * opens underneath it has not.
   */
  expanded?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}> &
  Omit<BoxProps<"button">, "as" | "children" | "disabled" | "onClick" | "value">;

/** A pressable row holds the hover wash while a popup it opened is up
 *  (`data-popup-open` is Base UI's open-trigger attribute), so the row a
 *  popover is anchored to still reads as the one being edited. */
const ROW_PRESSABLE_CLASS =
  "disabled:cursor-default data-popup-open:bg-wash-hover";

/**
 * A pressable row that opens something: a sub-page, a popover, a menu, or —
 * with `expanded` — its own content disclosed directly underneath it. The
 * trailing chevron is the affordance either way; passing `expanded` is what
 * turns it from a "goes somewhere" arrow into a disclosure that rotates.
 *
 * `...rest` forwards to the root button so the row itself can be an overlay's
 * trigger — Base UI takes `render={<OptionPanelNavRow … />}` and merges its
 * onClick / aria-expanded / data-popup-open / ref onto it. That is what makes
 * the whole row open the popover instead of a control parked on its edge.
 */
export function OptionPanelNavRow({
  label,
  icon,
  value,
  expanded,
  disabled,
  onClick,
  className,
  ...rest
}: OptionPanelNavRowProps) {
  return (
    <Box<"button">
      as="button"
      direction="row"
      align="center"
      gap="sm"
      minHeight={32}
      paddingX="sm"
      rounded="sm"
      hoverBackground={!disabled}
      opacity={disabled ? 50 : undefined}
      disabled={disabled}
      onClick={onClick}
      aria-expanded={expanded}
      {...rest}
      className={cn(ROW_PRESSABLE_CLASS, className)}
    >
      {icon ? <Icon name={icon} size="sm" color="muted" /> : null}
      <Box direction="column" shrink fill="flex" minWidth={0}>
        {typeof label === "string" ? (
          <Text size="sm" truncate={1}>
            {label}
          </Text>
        ) : (
          label
        )}
      </Box>
      {value !== undefined && value !== null ? (
        typeof value === "string" || typeof value === "number" ? (
          <Text size="xs" variant="faint">
            {value}
          </Text>
        ) : (
          value
        )
      ) : null}
      {/* The chevron sits on the readout's rung: faint, like the value it
          trails, so the trailing pair reads as one quiet unit under the
          label rather than a dark glyph beside a grey word. */}
      <Icon
        className={cn(
          "shrink-0 transition-transform duration-base ease-in-out",
          expanded && "rotate-90",
        )}
        color="faint"
        data-slot="option-panel-nav-indicator"
        name="chevron-right"
        size="sm"
      />
    </Box>
  );
}

export type OptionPanelActionRowProps = Omittable<{
  label: React.ReactNode;
  icon?: GlyphName;
  /** Paints the row's icon and label in the destructive hue, disabled
   *  included — the 50% dim alone marks the disabled state. */
  destructive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}> &
  Omit<BoxProps<"button">, "as" | "children" | "disabled" | "onClick">;

/** A pressable row that performs an action rather than navigating. */
export function OptionPanelActionRow({
  label,
  icon,
  destructive,
  disabled,
  onClick,
  className,
  ...rest
}: OptionPanelActionRowProps) {
  const tone = destructive ? "destructive" : "muted";
  return (
    <Box<"button">
      as="button"
      direction="row"
      align="center"
      gap="sm"
      minHeight={32}
      paddingX="sm"
      rounded="sm"
      hoverBackground={!disabled}
      opacity={disabled ? 50 : undefined}
      disabled={disabled}
      onClick={onClick}
      {...rest}
      className={cn(ROW_PRESSABLE_CLASS, className)}
    >
      {icon ? <Icon name={icon} size="sm" color={tone} /> : null}
      <Box direction="column" shrink fill="flex" minWidth={0}>
        {typeof label === "string" ? (
          <Text
            size="sm"
            variant={destructive ? "destructive" : undefined}
            truncate={1}
          >
            {label}
          </Text>
        ) : (
          label
        )}
      </Box>
    </Box>
  );
}
